import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import HeaderMain from "../dasboard-component/Components/HeaderMain";
import { useAuth } from "../context/AuthContext";
import { useInstituteAuth } from "../context/InstituteAuthContext";
import { toast } from "react-toastify";

/**
 * JSON shape we persist per basketball match
 * {
 *   matchType: "regular" | "friendly" | "tournament",
 *   userTeamName: string,
 *   opponentTeamName: string,
 *   userTeam: [{ id, userId, name, isCustom, position, number }],
 *   opponentTeam: [{ id, userId, name, isCustom, position, number }],
 *   quarters: [{ userTeamPoints, opponentTeamPoints }],
 *   overtimes: [{ userTeamPoints, opponentTeamPoints }],
 *   scoringEvents: [
 *     { id, team: "user" | "opponent", scorer, playerId, points, assist, assistId, quarter, time, shotType }
 *   ],
 *   foulEvents: [
 *     { id, team: "user" | "opponent", player, playerId, foulType, quarter, time }
 *   ],
 *   totalUserPoints: number,
 *   totalOpponentPoints: number,
 *   winner: "user" | "opponent" | null
 * }
 */

const brandBlue = "#3498db";
const brandBlueDark = "#2980b9";

export const AddBasketball = () => {
  const location = useLocation();
  const data = location.state || {};

  const [basketballMatches, setBasketballMatches] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { instituteUser } = useInstituteAuth();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Check if this is institute context
  const isInstitute = data.isInstitute || false;
  const instituteId = data.instituteId || instituteUser?.id;

  // Fetch available users for player selection
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/user-add-game/api/selection/users`,
          { credentials: "include" }
        );
        if (response.ok) {
          const users = await response.json();

          // Get current user ID
          const currentUserId = user?.uid;

          // Prioritize invited users and show their status
          const invitedUsers = (data.invitedUsers || []).map((user) => ({
            ...user,
            isInvited: true,
            invitationStatus: user.status,
          }));

          // Find current user and add them if not already in invited users
          const currentUser = users.find((user) => user.id == currentUserId);
          const currentUserIsInvited = invitedUsers.some(
            (invited) => invited.user_id == currentUserId
          );

          let finalUserList = [...invitedUsers];

          // Add current user if not already invited
          if (currentUser && !currentUserIsInvited) {
            finalUserList.push({
              ...currentUser,
              isCurrent: true,
            });
          }

          // Filter out invited users AND current user from the general list to avoid duplicates
          const otherUsers = users.filter(
            (user) =>
              !invitedUsers.some((invited) => invited.user_id === user.id) &&
              user.id != currentUserId
          );

          // Add other users
          finalUserList = [...finalUserList, ...otherUsers];

          setAvailableUsers(finalUserList);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, [data.invitedUsers]);

  // Tailwind class helpers for consistent styling
  const classes = {
    container:
      "max-w-6xl w-full mx-auto bg-white rounded-2xl shadow-lg font-sans mt-6 px-4 sm:px-6 lg:px-8 py-6 lg:py-8",
    header: "border-b-2 pb-4 mb-6",
    headerTitle: "text-3xl font-semibold text-gray-800 mb-2",
    headerSubtitle: "text-lg text-gray-600",
    addButton:
      "bg-[#3498db] hover:bg-[#2980b9] text-white font-semibold py-3 px-5 rounded-lg shadow-md transition-all duration-200 mb-5",
    saveButton:
      "w-full max-w-[260px] mx-auto block bg-emerald-500 text-white border-2 border-emerald-500 font-semibold py-3 px-4 rounded-lg shadow-md transition-all duration-200 mt-4 hover:bg-white hover:text-emerald-600 hover:-translate-y-0.5",
    noMatchesMessage:
      "text-center p-10 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 text-lg",
    guidanceBox:
      "bg-blue-50 border border-blue-200 rounded-lg p-5 shadow-sm mb-5",
    guidanceTitle:
      "text-lg font-semibold text-blue-800 mb-3 flex items-center gap-2",
    guidanceContent: "text-blue-700 space-y-2 text-sm leading-relaxed",
  };

  const validateMatches = () => {
    const errors = [];

    basketballMatches.forEach((match, index) => {
      // Check team names
      if (!match.userTeamName || !match.opponentTeamName) {
        errors.push(`Match ${index + 1}: Team names are required`);
      }

      // Check if teams have players
      if (match.userTeam.length === 0 || match.opponentTeam.length === 0) {
        errors.push(
          `Match ${index + 1}: Each team must have at least one player`
        );
      }

      // Check for minimum team size (at least 5 players for basketball)
      if (match.userTeam.length < 5) {
        errors.push(
          `Match ${
            index + 1
          }: Your team should have at least 5 players for a proper basketball match`
        );
      }

      if (match.opponentTeam.length < 5) {
        errors.push(
          `Match ${
            index + 1
          }: Opponent team should have at least 5 players for a proper basketball match`
        );
      }

      // Validate quarter scores
      match.quarters.forEach((quarter, qIndex) => {
        if (quarter.userTeamPoints < 0 || quarter.opponentTeamPoints < 0) {
          errors.push(
            `Match ${index + 1}, Quarter ${
              qIndex + 1
            }: Points cannot be negative`
          );
        }

        if (quarter.userTeamPoints > 50 || quarter.opponentTeamPoints > 50) {
          errors.push(
            `Match ${index + 1}, Quarter ${
              qIndex + 1
            }: Quarter points seem unreasonably high (max 50 per quarter)`
          );
        }
      });

      // Validate overtime scores if any
      match.overtimes.forEach((overtime, otIndex) => {
        if (overtime.userTeamPoints < 0 || overtime.opponentTeamPoints < 0) {
          errors.push(
            `Match ${index + 1}, Overtime ${
              otIndex + 1
            }: Points cannot be negative`
          );
        }

        if (overtime.userTeamPoints > 20 || overtime.opponentTeamPoints > 20) {
          errors.push(
            `Match ${index + 1}, Overtime ${
              otIndex + 1
            }: Overtime points seem unreasonably high (max 20 per overtime)`
          );
        }
      });

      // Validate scoring events (only if they have some data)
      match.scoringEvents.forEach((event, eventIndex) => {
        // Skip validation for events that only have default values
        if (
          !event.scorer ||
          event.scorer.trim() === "" ||
          event.scorer === "undefined"
        ) {
          return;
        }

        // Check if required fields are provided
        if (!event.points || !event.quarter) {
          errors.push(
            `Match ${index + 1}, Scoring Event ${
              eventIndex + 1
            }: Points and quarter are required`
          );
        }

        // Check if scorer belongs to the correct team
        const team =
          event.team === "user" ? match.userTeam : match.opponentTeam;
        const scorerExists = team.some(
          (player) => player.name === event.scorer
        );
        if (!scorerExists && event.scorer !== "Guest Player") {
          errors.push(
            `Match ${index + 1}, Scoring Event ${eventIndex + 1}: Scorer "${
              event.scorer
            }" is not in the ${
              event.team === "user" ? "your" : "opponent"
            } team`
          );
        }

        // Check valid point values (1, 2, or 3)
        if (event.points && ![1, 2, 3].includes(Number(event.points))) {
          errors.push(
            `Match ${index + 1}, Scoring Event ${
              eventIndex + 1
            }: Points must be 1, 2, or 3`
          );
        }
      });

      // Validate foul events (only if they have some data)
      match.foulEvents.forEach((foul, foulIndex) => {
        // Skip validation for events that only have default values
        if (
          !foul.player ||
          foul.player.trim() === "" ||
          foul.player === "undefined"
        ) {
          return;
        }

        // Check if foul type is provided
        if (!foul.foulType) {
          errors.push(
            `Match ${index + 1}, Foul Event ${
              foulIndex + 1
            }: Foul type is required`
          );
        }

        // Check if fouling player belongs to a team
        const userTeamPlayer = match.userTeam.some(
          (player) => player.name === foul.player
        );
        const opponentTeamPlayer = match.opponentTeam.some(
          (player) => player.name === foul.player
        );
        if (
          !userTeamPlayer &&
          !opponentTeamPlayer &&
          foul.player !== "Guest Player"
        ) {
          errors.push(
            `Match ${index + 1}, Foul Event ${foulIndex + 1}: Player "${
              foul.player
            }" is not in either team`
          );
        }
      });

      // Check if match has some scoring
      if (match.totalUserPoints === 0 && match.totalOpponentPoints === 0) {
        errors.push(
          `Match ${index + 1}: Match should have some scoring activity`
        );
      }

      // Check for tied game requiring overtime
      if (
        match.totalUserPoints === match.totalOpponentPoints &&
        match.overtimes.length === 0
      ) {
        errors.push(
          `Match ${index + 1}: Tied game requires overtime to determine winner`
        );
      }
    });

    return errors;
  };

  const handleSaveAll = async () => {
    const validationErrors = validateMatches();
    if (validationErrors.length > 0) {
      console.log(validationErrors);
      toast.error(
        "Please fix the following errors:\n\n" + validationErrors.join("\n")
      );
      return;
    }

    try {
      const arenaBookingId =
        data.arenaBooking?.id === "0" ? null : data.arenaBooking?.id;

      const bodyData = {
        arenaBookingId,
        sport: data.sport,
        games: basketballMatches,
      };

      // Add either userId or instituteId based on context
      if (isInstitute) {
        bodyData.instituteId = instituteId;
      } else {
        bodyData.userId = user?.uid;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/user-add-game/api/selection/save-match`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(bodyData),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save match");
      }

      const responseData = await response.json();
      toast.success("Saved all basketball matches!");

      // Navigate based on context
      if (isInstitute) {
        navigate("/institute-owner");
      } else {
        navigate("/Manage");
      }
    } catch (error) {
      console.error("Error saving match:", error);
      toast.error("Error saving match");
    }
  };

  const handleAddBasketballMatch = () => {
    const newMatch = {
      matchType: "regular",
      userTeamName: "",
      opponentTeamName: "",
      userTeam: [], // Array of player objects
      opponentTeam: [], // Array of player objects
      quarters: [
        { userTeamPoints: 0, opponentTeamPoints: 0 },
        { userTeamPoints: 0, opponentTeamPoints: 0 },
        { userTeamPoints: 0, opponentTeamPoints: 0 },
        { userTeamPoints: 0, opponentTeamPoints: 0 },
      ],
      overtimes: [],
      scoringEvents: [], // Detailed scoring events
      foulEvents: [], // Foul tracking
      totalUserPoints: 0,
      totalOpponentPoints: 0,
      winner: null,
    };
    setBasketballMatches((prev) => [...prev, newMatch]);
  };

  const handleRemoveMatch = (index) => {
    setBasketballMatches((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateMatch = (index, updatedMatch) => {
    setBasketballMatches((prevMatches) =>
      prevMatches.map((m, i) => (i === index ? updatedMatch : m))
    );
  };

  return (
    <>
      <HeaderMain pageType="AddGame" />

      <div className={classes.container}>
        <div className={classes.header} style={{ borderColor: brandBlue }}>
          <h2 className={classes.headerTitle}>
            {data.sport} Matches at {data.arenaBooking?.name}
          </h2>
          <p className={classes.headerSubtitle}>
            {data.arenaBooking?.date && data.arenaBooking?.time ? (
              <>
                Booking: {data.arenaBooking.date} at {data.arenaBooking.time}
              </>
            ) : (
              "Add detailed basketball match information"
            )}
          </p>
        </div>

        <button
          className={classes.addButton}
          onClick={handleAddBasketballMatch}
        >
          Add New Basketball Match
        </button>

        {basketballMatches.length === 0 && (
          <div className={classes.noMatchesMessage}>
            No matches added yet. Click the button above to add your first
            basketball match.
          </div>
        )}

        {basketballMatches.map((match, index) => (
          <BasketballMatchForm
            key={index}
            match={match}
            matchNumber={index + 1}
            availableUsers={availableUsers}
            isMobile={isMobile}
            onUpdate={(updatedMatch) => handleUpdateMatch(index, updatedMatch)}
            onRemove={() => handleRemoveMatch(index)}
          />
        ))}

        {basketballMatches.length > 0 && (
          <button onClick={handleSaveAll} className={classes.saveButton}>
            Save All Matches
          </button>
        )}
      </div>
    </>
  );
};

const BasketballMatchForm = ({
  match,
  matchNumber,
  availableUsers,
  isMobile,
  onUpdate,
  onRemove,
}) => {
  const [localMatch, setLocalMatch] = useState(match);
  const [activeTab, setActiveTab] = useState("basic"); // basic, teams, quarters, events

  const formClasses = {
    matchCard:
      "border border-gray-200 rounded-xl p-5 mb-6 bg-gray-50 shadow-sm",
    matchHeader:
      "flex justify-between items-start md:items-center flex-col md:flex-row gap-3 border-b border-gray-200 pb-4 mb-5",
    matchTitle: "text-xl font-semibold text-gray-800",
    removeButton:
      "bg-red-400 hover:bg-red-500 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200",
    tabContainer: "flex mb-5 border-b border-gray-200 flex-wrap",
    tab: "py-2 px-4 text-sm sm:text-base font-medium border-b-2 border-transparent bg-transparent transition-colors",
    activeTab: "",
    formGroup: "space-y-4",
    label: "font-medium text-gray-700",
    input:
      "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
    select:
      "px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
    addButton:
      "bg-[#3498db] hover:bg-[#2980b9] text-white font-medium px-3 py-2 rounded-lg transition-colors duration-200",
    teamSection: "bg-white p-5 rounded-lg shadow-sm mb-5",
    playerCard:
      "bg-gray-50 p-3 rounded-lg mb-3 flex justify-between items-center",
    tableWrapper: "w-full overflow-x-auto",
    table:
      "w-full text-sm border-collapse mt-4 rounded-lg overflow-hidden shadow-sm",
    tableHeader:
      "bg-gray-100 text-gray-800 font-semibold p-3 text-left border-b border-gray-200",
    tableCell: "p-3 border-b border-gray-200 bg-white",
    numberInput: "w-20 px-3 py-2 border border-gray-300 rounded text-center",
    scoreCard:
      "flex justify-between bg-white p-5 rounded-lg mt-5 shadow-sm flex-col md:flex-row gap-3",
    scoreBox:
      "text-center p-4 rounded-lg min-w-[120px] bg-gray-50 w-full md:w-auto",
    scoreLabel: "text-sm text-gray-500 mb-1",
    scoreValue: "text-3xl font-bold text-gray-800",
    winnerBox: "text-center p-4 rounded-lg bg-blue-50 min-w-[120px]",
    eventCard:
      "bg-white p-4 rounded-lg mb-3 border border-gray-200 grid gap-3 items-center" +
      (isMobile ? " grid-cols-1" : " grid-cols-[120px_1fr_1fr_80px_80px_auto]"),
    deleteButton: "bg-red-500 text-white px-2 py-1 rounded text-xs",
  };

  const updateParent = (updated) => {
    setLocalMatch(updated);
    onUpdate(updated);
  };

  const tabBaseStyle = {
    padding: "10px 16px",
    fontWeight: 600,
    fontSize: "16px",
    color: "#718096",
    border: "none",
    borderBottom: "2px solid transparent",
    background: "transparent",
    cursor: "pointer",
    transition: "all 0.2s ease",
  };

  const tabActiveStyle = {
    color: brandBlue,
    borderBottomColor: brandBlue,
  };

  // Player management functions (similar to football)
  const addPlayer = (team, userInfo, customName = "") => {
    // If it's a system user (not custom), check if already added to any team
    if (userInfo && userInfo.id) {
      const isInUserTeam = localMatch.userTeam.some(
        (player) => player.userId === userInfo.id
      );
      const isInOpponentTeam = localMatch.opponentTeam.some(
        (player) => player.userId === userInfo.id
      );

      if (isInUserTeam || isInOpponentTeam) {
        toast.error(
          `${userInfo.full_name || userInfo.username} is already added to ${
            isInUserTeam ? "your team" : "opponent team"
          }`
        );
        return;
      }
    }

    const updated = { ...localMatch };
    const player = {
      id: userInfo?.id || `custom_${Date.now()}`,
      userId: userInfo?.id || null, // Store actual user ID for statistics
      name: userInfo?.full_name || customName || "Guest Player",
      isCustom: !userInfo,
      position: "Guard", // Default position
      number: updated[team].length + 1,
    };

    updated[team].push(player);
    updateParent(updated);
  };

  const removePlayer = (team, playerIndex) => {
    const updated = { ...localMatch };
    updated[team].splice(playerIndex, 1);
    updateParent(updated);
  };

  // Quarter management
  const handleQuarterChange = (quarterIndex, team, value) => {
    // Validate and limit quarter scores
    const limitedValue = Math.max(0, Math.min(50, Number(value)));

    const updated = { ...localMatch };
    const fieldName = team === "user" ? "userTeamPoints" : "opponentTeamPoints";
    updated.quarters[quarterIndex][fieldName] = limitedValue;
    recalculateScores(updated);
  };

  const handleOvertimeChange = (overtimeIndex, team, value) => {
    // Validate and limit overtime scores
    const limitedValue = Math.max(0, Math.min(20, Number(value)));

    const updated = { ...localMatch };
    const fieldName = team === "user" ? "userTeamPoints" : "opponentTeamPoints";
    updated.overtimes[overtimeIndex][fieldName] = limitedValue;
    recalculateScores(updated);
  };

  const handleAddOvertime = () => {
    const updated = {
      ...localMatch,
      overtimes: [
        ...localMatch.overtimes,
        { userTeamPoints: 0, opponentTeamPoints: 0 },
      ],
    };
    updateParent(updated);
  };

  // Event management
  const addScoringEvent = () => {
    const updated = { ...localMatch };
    updated.scoringEvents.push({
      id: Date.now(),
      team: "user",
      scorer: "", // Changed from 'player' to 'scorer' to match validation
      playerId: null, // Store user ID for statistics
      points: 2, // 1, 2, or 3 points
      assist: "",
      assistId: null, // Store user ID for assists
      quarter: 1,
      time: "",
      shotType: "field_goal", // field_goal, three_pointer, free_throw
    });
    updateParent(updated);
  };

  const updateScoringEvent = (index, field, value) => {
    const updated = { ...localMatch };
    updated.scoringEvents[index][field] = value;

    // If updating scorer, also update playerId
    if (field === "scorer" && value) {
      const selectedPlayer = localMatch[
        updated.scoringEvents[index].team === "user"
          ? "userTeam"
          : "opponentTeam"
      ].find((player) => player.name === value);
      updated.scoringEvents[index].playerId = selectedPlayer?.userId || null;
    }

    // If updating assist, also update assistId
    if (field === "assist" && value) {
      const selectedPlayer = localMatch[
        updated.scoringEvents[index].team === "user"
          ? "userTeam"
          : "opponentTeam"
      ].find((player) => player.name === value);
      updated.scoringEvents[index].assistId = selectedPlayer?.userId || null;
    }

    updateParent(updated);
  };

  const removeScoringEvent = (index) => {
    const updated = { ...localMatch };
    updated.scoringEvents.splice(index, 1);
    updateParent(updated);
  };

  const addFoulEvent = () => {
    const updated = { ...localMatch };
    updated.foulEvents.push({
      id: Date.now(),
      team: "user",
      player: "",
      playerId: null, // Store user ID for statistics
      foulType: "personal", // personal, technical, flagrant
      quarter: 1,
      time: "",
    });
    updateParent(updated);
  };

  const updateFoulEvent = (index, field, value) => {
    const updated = { ...localMatch };
    updated.foulEvents[index][field] = value;

    // If updating player, also update playerId
    if (field === "player" && value) {
      const selectedPlayer = localMatch[
        updated.foulEvents[index].team === "user" ? "userTeam" : "opponentTeam"
      ].find((player) => player.name === value);
      updated.foulEvents[index].playerId = selectedPlayer?.userId || null;
    }

    updateParent(updated);
  };

  const removeFoulEvent = (index) => {
    const updated = { ...localMatch };
    updated.foulEvents.splice(index, 1);
    updateParent(updated);
  };

  const recalculateScores = (updatedMatch) => {
    let userTotal = 0;
    let opponentTotal = 0;

    // Sum quarters
    updatedMatch.quarters.forEach((quarter) => {
      userTotal += quarter.userTeamPoints;
      opponentTotal += quarter.opponentTeamPoints;
    });

    // Sum overtimes
    updatedMatch.overtimes.forEach((overtime) => {
      userTotal += overtime.userTeamPoints;
      opponentTotal += overtime.opponentTeamPoints;
    });

    updatedMatch.totalUserPoints = userTotal;
    updatedMatch.totalOpponentPoints = opponentTotal;

    // Determine winner
    if (userTotal > opponentTotal) {
      updatedMatch.winner = "user";
    } else if (opponentTotal > userTotal) {
      updatedMatch.winner = "opponent";
    } else {
      updatedMatch.winner = null;
    }

    updateParent(updatedMatch);
  };

  const renderBasicInfo = () => (
    <div>
      {/* Basketball Scoring Guidance */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 shadow-sm mb-5">
        <h4 className="text-lg font-semibold text-blue-800 mb-3 flex items-center gap-2">
          🏀 Basketball Match Guide
        </h4>
        <div className="text-blue-700 space-y-2 text-sm leading-relaxed">
          <p>
            <strong>Match Types:</strong> Regular, Friendly, Tournament
          </p>
          <p>
            <strong>Team Setup:</strong> Exactly 5 players on court (min 5 total
            players)
          </p>
          <p>
            <strong>Game Structure:</strong> 4 quarters + overtime if tied
          </p>
          <p>
            <strong>Scoring:</strong> Field Goal (2pts), Three-pointer (3pts),
            Free Throw (1pt)
          </p>
          <p>
            <strong>Quarter Limits:</strong> 0-50 points per quarter
          </p>
          <p>
            <strong>Overtime:</strong> Required if tied (0-20 points per OT)
          </p>
        </div>
      </div>

      <div className={formClasses.formGroup}>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-2">
            <label className={formClasses.label}>Your Team Name</label>
            <input
              type="text"
              value={localMatch.userTeamName}
              onChange={(e) =>
                updateParent({ ...localMatch, userTeamName: e.target.value })
              }
              className={formClasses.input}
              placeholder="Enter your team name"
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className={formClasses.label}>Opponent Team Name</label>
            <input
              type="text"
              value={localMatch.opponentTeamName}
              onChange={(e) =>
                updateParent({
                  ...localMatch,
                  opponentTeamName: e.target.value,
                })
              }
              className={formClasses.input}
              placeholder="Enter opponent team name"
            />
          </div>
        </div>
        <div className="flex flex-col gap-2 mt-3">
          <label className={formClasses.label}>Match Type</label>
          <select
            value={localMatch.matchType}
            onChange={(e) =>
              updateParent({ ...localMatch, matchType: e.target.value })
            }
            className={`${formClasses.select} w-full max-w-[240px]`}
          >
            <option value="regular">Regular</option>
            <option value="friendly">Friendly</option>
            <option value="tournament">Tournament</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderTeamManagement = () => (
    <div>
      {/* User Team */}
      <div className={formClasses.teamSection}>
        <h4 className="text-lg font-semibold text-gray-800 mb-2">
          Your Team: {localMatch.userTeamName || "Unnamed Team"} (
          {localMatch.userTeam.length}/5+ players)
        </h4>
        <PlayerSelector
          availableUsers={availableUsers}
          onAddPlayer={(userInfo, customName) =>
            addPlayer("userTeam", userInfo, customName)
          }
          selectedUserIds={[
            ...localMatch.userTeam.filter((p) => p.userId).map((p) => p.userId),
            ...localMatch.opponentTeam
              .filter((p) => p.userId)
              .map((p) => p.userId),
          ]}
        />
        <div className="mt-4 space-y-3">
          {localMatch.userTeam.map((player, index) => (
            <div key={player.id} className={formClasses.playerCard}>
              <span className="font-medium text-gray-800">
                #{player.number} {player.name} ({player.position}){" "}
                {player.isCustom && "(Custom)"}
              </span>
              <div className="flex items-center gap-2">
                <select
                  value={player.position}
                  onChange={(e) => {
                    const updated = { ...localMatch };
                    updated.userTeam[index].position = e.target.value;
                    updateParent(updated);
                  }}
                  className="px-2 py-1 border border-gray-300 rounded"
                >
                  <option value="Point Guard">Point Guard</option>
                  <option value="Shooting Guard">Shooting Guard</option>
                  <option value="Small Forward">Small Forward</option>
                  <option value="Power Forward">Power Forward</option>
                  <option value="Center">Center</option>
                </select>
                <button
                  onClick={() => removePlayer("userTeam", index)}
                  className={formClasses.deleteButton}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Opponent Team */}
      <div className={formClasses.teamSection}>
        <h4 className="text-lg font-semibold text-gray-800 mb-2">
          Opponent Team: {localMatch.opponentTeamName || "Unnamed Team"} (
          {localMatch.opponentTeam.length}/5+ players)
        </h4>
        <PlayerSelector
          availableUsers={availableUsers}
          onAddPlayer={(userInfo, customName) =>
            addPlayer("opponentTeam", userInfo, customName)
          }
          selectedUserIds={[
            ...localMatch.userTeam.filter((p) => p.userId).map((p) => p.userId),
            ...localMatch.opponentTeam
              .filter((p) => p.userId)
              .map((p) => p.userId),
          ]}
        />
        <div className="mt-4 space-y-3">
          {localMatch.opponentTeam.map((player, index) => (
            <div key={player.id} className={formClasses.playerCard}>
              <span className="font-medium text-gray-800">
                #{player.number} {player.name} ({player.position}){" "}
                {player.isCustom && "(Custom)"}
              </span>
              <div className="flex items-center gap-2">
                <select
                  value={player.position}
                  onChange={(e) => {
                    const updated = { ...localMatch };
                    updated.opponentTeam[index].position = e.target.value;
                    updateParent(updated);
                  }}
                  className="px-2 py-1 border border-gray-300 rounded"
                >
                  <option value="Point Guard">Point Guard</option>
                  <option value="Shooting Guard">Shooting Guard</option>
                  <option value="Small Forward">Small Forward</option>
                  <option value="Power Forward">Power Forward</option>
                  <option value="Center">Center</option>
                </select>
                <button
                  onClick={() => removePlayer("opponentTeam", index)}
                  className={formClasses.deleteButton}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderQuarters = () => (
    <div>
      {/* Quarters Table */}
      <div className={formClasses.tableWrapper}>
        <table className={formClasses.table}>
          <thead>
            <tr>
              <th className={formClasses.tableHeader}>Quarter</th>
              <th className={formClasses.tableHeader}>
                {localMatch.userTeamName || "Your Team"} Points
              </th>
              <th className={formClasses.tableHeader}>
                {localMatch.opponentTeamName || "Opponent"} Points
              </th>
            </tr>
          </thead>
          <tbody>
            {localMatch.quarters.map((quarter, i) => (
              <tr key={i}>
                <td
                  className={`${formClasses.tableCell} font-semibold bg-gray-50`}
                >
                  Q{i + 1}
                </td>
                <td className={formClasses.tableCell}>
                  <input
                    type="number"
                    value={quarter.userTeamPoints}
                    onChange={(e) =>
                      handleQuarterChange(i, "user", e.target.value)
                    }
                    min="0"
                    className={formClasses.numberInput}
                  />
                </td>
                <td className={formClasses.tableCell}>
                  <input
                    type="number"
                    value={quarter.opponentTeamPoints}
                    onChange={(e) =>
                      handleQuarterChange(i, "opponent", e.target.value)
                    }
                    min="0"
                    className={formClasses.numberInput}
                  />
                </td>
              </tr>
            ))}
            {/* Overtime rows */}
            {localMatch.overtimes.map((overtime, i) => (
              <tr key={`ot-${i}`}>
                <td
                  className={`${formClasses.tableCell} font-semibold bg-orange-50`}
                >
                  OT{i + 1}
                </td>
                <td className={formClasses.tableCell}>
                  <input
                    type="number"
                    value={overtime.userTeamPoints}
                    onChange={(e) =>
                      handleOvertimeChange(i, "user", e.target.value)
                    }
                    min="0"
                    className={formClasses.numberInput}
                  />
                </td>
                <td className={formClasses.tableCell}>
                  <input
                    type="number"
                    value={overtime.opponentTeamPoints}
                    onChange={(e) =>
                      handleOvertimeChange(i, "opponent", e.target.value)
                    }
                    min="0"
                    className={formClasses.numberInput}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button
        onClick={handleAddOvertime}
        className={`${formClasses.addButton} mt-3`}
      >
        Add Overtime Period
      </button>
    </div>
  );

  const renderEvents = () => (
    <div>
      {/* Scoring Events */}
      <div className={formClasses.teamSection}>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-gray-800">Scoring Events</h4>
          <button onClick={addScoringEvent} className={formClasses.addButton}>
            Add Score
          </button>
        </div>
        {localMatch.scoringEvents.map((event, index) => (
          <div key={event.id} className={formClasses.eventCard}>
            <select
              value={event.team}
              onChange={(e) =>
                updateScoringEvent(index, "team", e.target.value)
              }
              className={formClasses.select}
            >
              <option value="user">
                {localMatch.userTeamName || "Your Team"}
              </option>
              <option value="opponent">
                {localMatch.opponentTeamName || "Opponent"}
              </option>
            </select>
            <select
              value={event.scorer}
              onChange={(e) =>
                updateScoringEvent(index, "scorer", e.target.value)
              }
              className={formClasses.input}
            >
              <option value="">Select player</option>
              {event.team === "user" ? (
                localMatch.userTeam.length > 0 ? (
                  localMatch.userTeam.map((player) => (
                    <option key={player.id} value={player.name}>
                      {player.name}
                    </option>
                  ))
                ) : (
                  <option value="Guest Player">Guest Player</option>
                )
              ) : localMatch.opponentTeam.length > 0 ? (
                localMatch.opponentTeam.map((player) => (
                  <option key={player.id} value={player.name}>
                    {player.name}
                  </option>
                ))
              ) : (
                <option value="Guest Player">Guest Player</option>
              )}
            </select>
            <select
              value={event.assist}
              onChange={(e) =>
                updateScoringEvent(index, "assist", e.target.value)
              }
              className={formClasses.input}
            >
              <option value="">Select assist (optional)</option>
              {event.team === "user" ? (
                localMatch.userTeam.length > 0 ? (
                  localMatch.userTeam.map((player) => (
                    <option key={player.id} value={player.name}>
                      {player.name}
                    </option>
                  ))
                ) : (
                  <option value="Guest Player">Guest Player</option>
                )
              ) : localMatch.opponentTeam.length > 0 ? (
                localMatch.opponentTeam.map((player) => (
                  <option key={player.id} value={player.name}>
                    {player.name}
                  </option>
                ))
              ) : (
                <option value="Guest Player">Guest Player</option>
              )}
            </select>
            <select
              value={event.points}
              onChange={(e) =>
                updateScoringEvent(index, "points", Number(e.target.value))
              }
              className={`${formClasses.select} min-w-[70px]`}
            >
              <option value={1}>1 pt</option>
              <option value={2}>2 pts</option>
              <option value={3}>3 pts</option>
            </select>
            <select
              value={event.quarter}
              onChange={(e) =>
                updateScoringEvent(index, "quarter", Number(e.target.value))
              }
              className={`${formClasses.select} min-w-[70px]`}
            >
              <option value={1}>Q1</option>
              <option value={2}>Q2</option>
              <option value={3}>Q3</option>
              <option value={4}>Q4</option>
              {localMatch.overtimes.map((_, i) => (
                <option key={`ot-${i}`} value={5 + i}>
                  OT{i + 1}
                </option>
              ))}
            </select>
            <button
              onClick={() => removeScoringEvent(index)}
              className={formClasses.deleteButton}
            >
              Delete
            </button>
          </div>
        ))}
      </div>

      {/* Foul Events */}
      <div className={formClasses.teamSection}>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-gray-800">Fouls</h4>
          <button onClick={addFoulEvent} className={formClasses.addButton}>
            Add Foul
          </button>
        </div>
        {localMatch.foulEvents.map((foul, index) => (
          <div key={foul.id} className={formClasses.eventCard}>
            <select
              value={foul.team}
              onChange={(e) => updateFoulEvent(index, "team", e.target.value)}
              className={formClasses.select}
            >
              <option value="user">
                {localMatch.userTeamName || "Your Team"}
              </option>
              <option value="opponent">
                {localMatch.opponentTeamName || "Opponent"}
              </option>
            </select>
            <select
              value={foul.player}
              onChange={(e) => updateFoulEvent(index, "player", e.target.value)}
              className={formClasses.input}
            >
              <option value="">Select player</option>
              {foul.team === "user" ? (
                localMatch.userTeam.length > 0 ? (
                  localMatch.userTeam.map((player) => (
                    <option key={player.id} value={player.name}>
                      {player.name}
                    </option>
                  ))
                ) : (
                  <option value="Guest Player">Guest Player</option>
                )
              ) : localMatch.opponentTeam.length > 0 ? (
                localMatch.opponentTeam.map((player) => (
                  <option key={player.id} value={player.name}>
                    {player.name}
                  </option>
                ))
              ) : (
                <option value="Guest Player">Guest Player</option>
              )}
            </select>
            <select
              value={foul.foulType}
              onChange={(e) =>
                updateFoulEvent(index, "foulType", e.target.value)
              }
              className={formClasses.select}
            >
              <option value="personal">Personal</option>
              <option value="technical">Technical</option>
              <option value="flagrant">Flagrant</option>
              <option value="offensive">Offensive</option>
            </select>
            <select
              value={foul.quarter}
              onChange={(e) =>
                updateFoulEvent(index, "quarter", Number(e.target.value))
              }
              className={`${formClasses.select} min-w-[70px]`}
            >
              <option value={1}>Q1</option>
              <option value={2}>Q2</option>
              <option value={3}>Q3</option>
              <option value={4}>Q4</option>
              {localMatch.overtimes.map((_, i) => (
                <option key={`ot-${i}`} value={5 + i}>
                  OT{i + 1}
                </option>
              ))}
            </select>
            <input
              type="text"
              value={foul.time}
              onChange={(e) => updateFoulEvent(index, "time", e.target.value)}
              placeholder="Time"
              className={`${formClasses.input} max-w-[80px]`}
            />
            <button
              onClick={() => removeFoulEvent(index)}
              className={formClasses.deleteButton}
            >
              Delete
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className={formClasses.matchCard}>
      <div className={formClasses.matchHeader}>
        <h3 className={formClasses.matchTitle}>
          Basketball Match #{matchNumber}
        </h3>
        <button onClick={onRemove} className={formClasses.removeButton}>
          Remove Match
        </button>
      </div>

      <div className={formClasses.tabContainer}>
        {["basic", "teams", "quarters", "events"].map((tab) => (
          <button
            key={tab}
            className={formClasses.tab}
            style={{
              ...tabBaseStyle,
              ...(activeTab === tab ? tabActiveStyle : {}),
            }}
            onClick={() => setActiveTab(tab)}
          >
            {tab === "basic"
              ? "Basic Info"
              : tab === "teams"
              ? "Teams"
              : tab === "quarters"
              ? "Quarters"
              : "Events"}
          </button>
        ))}
      </div>

      {activeTab === "basic" && renderBasicInfo()}
      {activeTab === "teams" && renderTeamManagement()}
      {activeTab === "quarters" && renderQuarters()}
      {activeTab === "events" && renderEvents()}

      {/* Final Score Display */}
      <div className={formClasses.scoreCard}>
        <div className={formClasses.scoreBox}>
          <div className={formClasses.scoreLabel}>
            {localMatch.userTeamName || "Your Team"}
          </div>
          <div className={formClasses.scoreValue}>
            {localMatch.totalUserPoints}
          </div>
        </div>

        <div className={formClasses.winnerBox}>
          <div className={formClasses.scoreLabel}>Result</div>
          <div className="text-base font-semibold text-blue-800">
            {localMatch.winner === null
              ? "Tie"
              : localMatch.winner === "user"
              ? "Win"
              : "Loss"}
          </div>
        </div>

        <div className={formClasses.scoreBox}>
          <div className={formClasses.scoreLabel}>
            {localMatch.opponentTeamName || "Opponent"}
          </div>
          <div className={formClasses.scoreValue}>
            {localMatch.totalOpponentPoints}
          </div>
        </div>
      </div>
    </div>
  );
};

// Reuse PlayerSelector component from Football
const PlayerSelector = ({
  availableUsers,
  onAddPlayer,
  selectedUserIds = [],
}) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [customName, setCustomName] = useState("");
  const [useCustomName, setUseCustomName] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Fetch user suggestions from API
  const fetchUserSuggestions = async (term) => {
    if (!term || term.trim().length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/common/search-users?term=${term}`
      );
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
      }
    } catch (error) {
      console.error("Error fetching user suggestions:", error);
      setSuggestions([]);
    }
  };

  // Debounced search
  useEffect(() => {
    if (!useCustomName && searchTerm) {
      const timer = setTimeout(() => {
        fetchUserSuggestions(searchTerm);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setSuggestions([]);
    }
  }, [searchTerm, useCustomName]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setShowSuggestions(true);
    setSelectedUser(null);
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchTerm(user.full_name || user.username);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleAdd = () => {
    if (useCustomName && customName.trim()) {
      onAddPlayer(null, customName.trim());
      setCustomName("");
    } else if (!useCustomName && selectedUser) {
      onAddPlayer(selectedUser);
      setSelectedUser(null);
      setSearchTerm("");
    }
  };

  const ps = {
    container: "flex flex-wrap gap-3 items-center relative",
    searchContainer: "relative flex-1 min-w-[250px]",
    input:
      "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm",
    suggestionsDropdown:
      "absolute top-full left-0 right-0 bg-white border border-gray-300 border-t-0 rounded-b-lg max-h-[200px] overflow-y-auto z-[1000] shadow-lg",
    suggestionItem:
      "px-3 py-2 border-b border-gray-100 transition-colors text-sm",
    button:
      "bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 text-sm",
    toggle: "text-blue-600 hover:text-blue-800 text-sm underline",
    noResults: "px-3 py-2 text-sm text-gray-600 text-center",
  };

  return (
    <div className={ps.container}>
      {!useCustomName ? (
        <>
          <div className={ps.searchContainer}>
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => {
                // Delay to allow click on suggestion
                setTimeout(() => setShowSuggestions(false), 200);
              }}
              placeholder="Search for system users..."
              className={ps.input}
            />
            {showSuggestions && suggestions.length > 0 && (
              <div className={ps.suggestionsDropdown}>
                {suggestions.map((user) => {
                  const isAlreadySelected = selectedUserIds.includes(
                    user.id || user.user_id
                  );
                  return (
                    <div
                      key={user.id || user.user_id}
                      className={`${ps.suggestionItem} ${
                        isAlreadySelected
                          ? "opacity-50 cursor-not-allowed bg-gray-100"
                          : "cursor-pointer"
                      }`}
                      onClick={() =>
                        !isAlreadySelected && handleSelectUser(user)
                      }
                    >
                      <div className="font-medium">
                        {user.full_name || user.username}
                        {isAlreadySelected && (
                          <span className="text-red-500 ml-2">
                            (Already Added)
                          </span>
                        )}
                      </div>
                      {user.email && (
                        <div className="text-xs text-gray-600">
                          {user.email}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {showSuggestions &&
              searchTerm.length >= 2 &&
              suggestions.length === 0 && (
                <div className={ps.suggestionsDropdown}>
                  <div className={ps.noResults}>No users found</div>
                </div>
              )}
          </div>
          <button
            onClick={handleAdd}
            disabled={!selectedUser}
            className={`${ps.button} ${
              selectedUser ? "" : "opacity-50 cursor-not-allowed"
            }`}
          >
            Add Player
          </button>
        </>
      ) : (
        <>
          <input
            type="text"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder="Enter custom player name"
            className={ps.input}
          />
          <button
            onClick={handleAdd}
            disabled={!customName.trim()}
            className={`${ps.button} ${
              customName.trim() ? "" : "opacity-50 cursor-not-allowed"
            }`}
          >
            Add Player
          </button>
        </>
      )}
      <span
        onClick={() => setUseCustomName(!useCustomName)}
        className={ps.toggle}
      >
        {useCustomName ? "Use system users" : "Add custom name"}
      </span>
    </div>
  );
};

export default AddBasketball;
